import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { nextgenConversationPath, nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import {
  type EditSession,
  appEditSessionPath,
  projectAppPath,
  projectAppsPath,
} from "~/services/apps";
import { apiMutate, getApiErrorMessage, isApiError } from "~/utils/apiClient";
import type { OpenAppState } from "./PublishedAppPage";

interface PublishedAppActionsProps {
  // The project as it appears in the route, and its UUID, which the
  // analysis-agent's routes take.
  projectId: string;
  projectUuid: string;
  slug: string;
  title: string;
}

/** A manager's actions on a published app: edit it in a new session, or unpublish it. */
export const PublishedAppActions = ({
  projectId,
  projectUuid,
  slug,
  title,
}: PublishedAppActionsProps) => (
  <>
    <EditAppButton projectId={projectId} projectUuid={projectUuid} slug={slug} />
    <UnpublishAppButton projectId={projectId} slug={slug} title={title} />
  </>
);

// Starts a session holding a draft of the app, and enters it with the draft open.
const EditAppButton = ({
  projectId,
  projectUuid,
  slug,
}: Omit<PublishedAppActionsProps, "title">) => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [isStarting, setStarting] = useState(false);

  const handleEdit = async () => {
    setStarting(true);
    try {
      const session = await apiMutate<EditSession>(appEditSessionPath(projectUuid, slug), "POST");
      if (!session) {
        throw new Error("No session was created");
      }
      const state: OpenAppState = { openArtifactAppId: session.template_artifact_id };
      navigate(nextgenConversationPath(projectId, session.conversation_id), { state });
    } catch (e) {
      showError(isApiError(e) ? getApiErrorMessage(e) : "Couldn't start editing this app.");
      setStarting(false);
    }
  };

  return (
    <IconAction label="Edit app" onClick={() => void handleEdit()} disabled={isStarting}>
      {isStarting ? <Loader2 className="size-4 animate-spin" /> : <Pencil className="size-4" />}
    </IconAction>
  );
};

// Unpublishing deletes the app's row and its published state; the sessions it
// was built in keep their drafts, so it can be published again from there.
const UnpublishAppButton = ({
  projectId,
  slug,
  title,
}: Pick<PublishedAppActionsProps, "projectId" | "slug" | "title">) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [isConfirming, setConfirming] = useState(false);

  const handleUnpublish = async () => {
    try {
      await apiMutate(projectAppPath(projectId, slug), "DELETE", undefined, { expects204: true });
    } catch (e) {
      showError(isApiError(e) ? getApiErrorMessage(e) : "Couldn't unpublish this app.");
      throw e;
    }
    await mutate(projectAppsPath(projectId));
    showSuccess(`Unpublished “${title}”`);
    navigate(nextgenProjectPath(projectId));
  };

  return (
    <>
      <IconAction label="Unpublish app" onClick={() => setConfirming(true)}>
        <Trash2 className="size-4" />
      </IconAction>
      <ConfirmDestructiveDialog
        target={isConfirming ? title : null}
        title="Unpublish this app?"
        description={(name) => (
          <>
            <span className="font-medium text-foreground">{name}</span> will be removed from the
            project, along with everything saved in it, for every member. Publishing it again starts
            with empty state. This cannot be undone.
          </>
        )}
        confirmLabel="Unpublish"
        pendingLabel="Unpublishing…"
        onConfirm={handleUnpublish}
        onClose={() => setConfirming(false)}
      />
    </>
  );
};

const IconAction = ({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className="size-8"
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);
